type JsonRecord = Record<string, unknown>;

export interface OptimisticMonthlyAllocation {
  pricing_line_id: string;
  ym: string;
  allocated_qty: number;
  billing_uo?: string;
  production_load_jh?: number;
}

export interface SessionAllocationPatch {
  entityId: string;
  mode: 'AUTO' | 'MANUAL';
  pricingLineId?: string;
  month?: string;
  allocatedQty?: number;
  expectedRevision?: string | number;
  optimisticProductionLoadJh?: number;
  /** Full AUTO preview used while the atomic AUTO -> MANUAL receipt is pending. */
  optimisticMonthlyAllocations?: OptimisticMonthlyAllocation[];
}

export interface SessionAllocationOverlay extends SessionAllocationPatch {
  token: string;
  epoch: number;
  committed: boolean;
  createdAt: number;
  canonicalRevision?: string | number;
  canonicalMonthlyAllocations?: JsonRecord[];
}

const overlays = new Map<string, SessionAllocationOverlay>();
let epoch = 0;

function text(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function sessionIdOf(value: JsonRecord): string {
  return text(value['fibery/id'] || value.id);
}

function isSessionEventProjection(value: JsonRecord): boolean {
  return text(value.entity_type).toLowerCase() === 'sessions'
    || text(value.kind).toLowerCase() === 'session';
}

function overlayKey(value: SessionAllocationPatch): string {
  return value.pricingLineId && value.month
    ? `${value.entityId}:${value.pricingLineId}:${value.month}`
    : `${value.entityId}:mode`;
}

function allocationMatches(allocation: JsonRecord, patch: SessionAllocationPatch): boolean {
  return text(allocation.pricing_line_id) === patch.pricingLineId
    && text(allocation.ym) === patch.month;
}

function revisionAtLeast(current: unknown, expected: unknown): boolean {
  if (expected === undefined || expected === null || expected === '') return true;
  const currentNumber = Number(current);
  const expectedNumber = Number(expected);
  if (Number.isFinite(currentNumber) && Number.isFinite(expectedNumber)) {
    return currentNumber >= expectedNumber;
  }
  return text(current) === text(expected);
}

function monotoneRevision(current: unknown, canonical: unknown): string | number | undefined {
  if (canonical === undefined || canonical === null || canonical === '') {
    return typeof current === 'string' || typeof current === 'number' ? current : undefined;
  }
  const currentNumber = Number(current);
  const canonicalNumber = Number(canonical);
  if (Number.isFinite(currentNumber) && Number.isFinite(canonicalNumber)) {
    return Math.max(currentNumber, canonicalNumber);
  }
  return canonical as string | number;
}

function normalizedSeedAllocations(
  allocations: OptimisticMonthlyAllocation[],
  sessionId: string,
  token?: string,
): JsonRecord[] {
  return allocations.map((allocation) => {
    const [yearRaw, monthRaw] = allocation.ym.split('-');
    return {
      ...allocation,
      session_id: sessionId,
      year: Number(yearRaw),
      month: Number(monthRaw),
      ...(token ? { _optimistic_seed_token: token } : {}),
    };
  });
}

function sessionAlreadyContains(session: JsonRecord, patch: SessionAllocationPatch): boolean {
  if (sessionIdOf(session) !== patch.entityId) return false;
  const overlay = patch as Partial<SessionAllocationOverlay>;
  if (!revisionAtLeast(session.monthly_allocation_revision, overlay.canonicalRevision)) return false;
  if (text(session.monthly_allocation_mode).toUpperCase() !== patch.mode) return false;
  if (!patch.pricingLineId || !patch.month || patch.allocatedQty === undefined) return true;
  const allocations = Array.isArray(session.monthly_allocations) ? session.monthly_allocations as JsonRecord[] : [];
  const current = allocations.find((allocation) => allocationMatches(allocation, patch));
  return !!current && Math.abs(Number(current.allocated_qty || 0) - patch.allocatedQty) <= 1e-6;
}

function patchSession(session: JsonRecord, patch: SessionAllocationPatch, token?: string): JsonRecord {
  if (sessionIdOf(session) !== patch.entityId) return session;
  const overlay = patch as Partial<SessionAllocationOverlay>;
  const patchEpoch = overlay.epoch ?? epoch;
  const nextRevision = monotoneRevision(session.monthly_allocation_revision, overlay.canonicalRevision);
  const revisionPatch = nextRevision !== undefined ? { monthly_allocation_revision: nextRevision } : {};
  if (patch.mode === 'AUTO' && !patch.pricingLineId) {
    return { ...session, monthly_allocation_mode: 'AUTO', monthly_allocations: [], ...revisionPatch, _client_allocation_epoch: patchEpoch };
  }
  if (!patch.pricingLineId || !patch.month || patch.allocatedQty === undefined) {
    return {
      ...session,
      monthly_allocation_mode: patch.mode,
      ...(overlay.canonicalMonthlyAllocations ? { monthly_allocations: overlay.canonicalMonthlyAllocations } : {}),
      ...revisionPatch,
      _client_allocation_epoch: patchEpoch,
    };
  }
  const currentAllocations = Array.isArray(session.monthly_allocations) ? session.monthly_allocations as JsonRecord[] : [];
  const allocations = overlay.canonicalMonthlyAllocations
    || (patch.optimisticMonthlyAllocations?.length
      ? normalizedSeedAllocations(patch.optimisticMonthlyAllocations, patch.entityId, token)
      : currentAllocations);
  const [yearRaw, monthRaw] = patch.month.split('-');
  const previous = allocations.find((allocation) => allocationMatches(allocation, patch));
  const nextAllocation: JsonRecord = {
    ...(previous || {}),
    session_id: patch.entityId,
    pricing_line_id: patch.pricingLineId,
    ym: patch.month,
    year: Number(yearRaw),
    month: Number(monthRaw),
    allocated_qty: patch.allocatedQty,
    ...(patch.optimisticProductionLoadJh !== undefined ? { production_load_jh: patch.optimisticProductionLoadJh } : {}),
    ...(token ? { _optimistic_token: token } : {}),
  };
  return {
    ...session,
    monthly_allocation_mode: 'MANUAL',
    monthly_allocations: previous
      ? allocations.map((allocation) => allocationMatches(allocation, patch) ? nextAllocation : allocation)
      : [...allocations, nextAllocation],
    ...revisionPatch,
    _client_allocation_epoch: patchEpoch,
  };
}

function mapSessionArrays(payload: unknown, mapper: (session: JsonRecord) => JsonRecord): unknown {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const source = payload as JsonRecord;
  const patchArray = (value: unknown) => Array.isArray(value)
    ? value.map((entry) => entry && typeof entry === 'object' && !Array.isArray(entry) ? mapper(entry as JsonRecord) : entry)
    : value;
  const next: JsonRecord = { ...source, entities: patchArray(source.entities) };
  if (!source.lookups || typeof source.lookups !== 'object' || Array.isArray(source.lookups)) return next;
  const lookups = source.lookups as JsonRecord;
  const nextLookups: JsonRecord = { ...lookups, sessions: patchArray(lookups.sessions) };
  if (lookups.sessions_by_mission && typeof lookups.sessions_by_mission === 'object' && !Array.isArray(lookups.sessions_by_mission)) {
    nextLookups.sessions_by_mission = Object.fromEntries(
      Object.entries(lookups.sessions_by_mission as JsonRecord).map(([missionId, sessions]) => [missionId, patchArray(sessions)]),
    );
  }
  if (lookups.session_meta && typeof lookups.session_meta === 'object' && !Array.isArray(lookups.session_meta)) {
    const meta = lookups.session_meta as JsonRecord;
    nextLookups.session_meta = { ...meta, entities: patchArray(meta.entities) };
  }
  next.lookups = nextLookups;
  return next;
}

function payloadContains(payload: unknown, patch: SessionAllocationPatch): boolean {
  let found = false;
  let everyProjectionMatches = true;
  mapSessionArrays(payload, (session) => {
    const carriesAllocationProjection = Object.prototype.hasOwnProperty.call(session, 'monthly_allocation_mode')
      || Array.isArray(session.monthly_allocations);
    if (sessionIdOf(session) === patch.entityId && carriesAllocationProjection) {
      found = true;
      if (!sessionAlreadyContains(session, patch)) everyProjectionMatches = false;
    }
    return session;
  });
  return found && everyProjectionMatches;
}

export function beginSessionAllocationOverlay(patch: SessionAllocationPatch): SessionAllocationOverlay {
  epoch += 1;
  const overlay: SessionAllocationOverlay = {
    ...patch,
    token: `allocation-${epoch}-${Date.now()}`,
    epoch,
    committed: false,
    createdAt: Date.now(),
  };
  overlays.set(overlayKey(overlay), overlay);
  return overlay;
}

export function commitSessionAllocationOverlay(overlay: SessionAllocationOverlay, canonical?: JsonRecord): void {
  const current = overlays.get(overlayKey(overlay));
  if (current?.token !== overlay.token) return;
  current.committed = true;
  if (!canonical) return;
  const revision = canonical.monthly_allocation_revision ?? canonical.revision;
  if (typeof revision === 'string' || typeof revision === 'number') current.canonicalRevision = revision;
  if (Array.isArray(canonical.monthly_allocations)) {
    current.canonicalMonthlyAllocations = canonical.monthly_allocations as JsonRecord[];
  }
}

export function failSessionAllocationOverlay(overlay: SessionAllocationOverlay): void {
  const current = overlays.get(overlayKey(overlay));
  if (current?.token === overlay.token) overlays.delete(overlayKey(overlay));
}

export function patchSessionAllocationPayload(payload: unknown, patch: SessionAllocationPatch, token?: string): unknown {
  return mapSessionArrays(payload, (session) => patchSession(session, patch, token));
}

export function mergeCanonicalSessionPayload(payload: unknown, canonical: JsonRecord): unknown {
  const canonicalId = sessionIdOf(canonical);
  if (!canonicalId) return payload;
  const merged = mapSessionArrays(payload, (session) => (
    sessionIdOf(session) === canonicalId ? { ...session, ...canonical } : session
  ));
  // A mutation receipt is authoritative for the changed session, but it is not
  // the projection revalidation itself. Keep every overlay alive until a real
  // GET confirms it; otherwise a late pre-mutation GET can still win directly
  // after this merge.
  return applySessionAllocationOverlays(merged, { confirmCommitted: false });
}

/**
 * Applied at the network boundary. A stale GET cannot erase a committed local
 * write: overlays remain until a server projection actually contains the same
 * value. Independent cells have independent monotone epochs.
 */
export function applySessionAllocationOverlays(
  payload: unknown,
  options: { confirmCommitted?: boolean } = {},
): unknown {
  let next = payload;
  const now = Date.now();
  [...overlays.values()].sort((left, right) => left.epoch - right.epoch).forEach((overlay) => {
    if (options.confirmCommitted !== false && overlay.committed && payloadContains(next, overlay)) {
      overlays.delete(overlayKey(overlay));
      return;
    }
    // Safety valve only for abandoned browser state; normal overlays disappear
    // as soon as the canonical projection confirms the value.
    if (overlay.committed && now - overlay.createdAt > 5 * 60_000) {
      overlays.delete(overlayKey(overlay));
      return;
    }
    next = patchSessionAllocationPayload(next, overlay, overlay.token);
  });
  return next;
}

export function rollbackSessionAllocationPayload(
  currentPayload: unknown,
  snapshotPayload: unknown,
  overlay: SessionAllocationOverlay,
): unknown {
  const previousSessions: JsonRecord[] = [];
  mapSessionArrays(snapshotPayload, (session) => {
    if (sessionIdOf(session) === overlay.entityId) previousSessions.push(session);
    return session;
  });
  if (!previousSessions.length) return currentPayload;
  return mapSessionArrays(currentPayload, (session) => {
    if (sessionIdOf(session) !== overlay.entityId) return session;
    const previousSession = previousSessions.find((candidate) => (
      isSessionEventProjection(candidate) === isSessionEventProjection(session)
    )) || previousSessions[0];
    if (!overlay.pricingLineId || !overlay.month) {
      return Number(session._client_allocation_epoch) === overlay.epoch ? previousSession : session;
    }
    const allocations = Array.isArray(session.monthly_allocations) ? session.monthly_allocations as JsonRecord[] : [];
    if (overlay.optimisticMonthlyAllocations?.length && Number(session._client_allocation_epoch) === overlay.epoch) {
      return previousSession as JsonRecord;
    }
    const currentAllocation = allocations.find((allocation) => allocationMatches(allocation, overlay));
    if (text(currentAllocation?._optimistic_token) !== overlay.token) return session;
    const previousAllocations = Array.isArray(previousSession.monthly_allocations)
      ? previousSession.monthly_allocations as JsonRecord[]
      : [];
    const previousAllocation = previousAllocations.find((allocation) => allocationMatches(allocation, overlay));
    let restored = previousAllocation
      ? allocations.map((allocation) => allocationMatches(allocation, overlay) ? previousAllocation as JsonRecord : allocation)
      : allocations.filter((allocation) => !allocationMatches(allocation, overlay));
    if (overlay.optimisticMonthlyAllocations?.length) {
      restored = restored.flatMap((allocation) => {
        if (text(allocation._optimistic_seed_token) !== overlay.token) return [allocation];
        if (text(allocation._optimistic_token) && text(allocation._optimistic_token) !== overlay.token) return [allocation];
        const previous = previousAllocations.find((candidate) => (
          text(candidate.pricing_line_id) === text(allocation.pricing_line_id)
          && text(candidate.ym) === text(allocation.ym)
        ));
        return previous ? [previous] : [];
      });
    }
    const hasSurvivingOptimisticWrite = restored.some((allocation) => {
      const allocationToken = text(allocation._optimistic_token);
      return allocationToken && allocationToken !== overlay.token;
    });
    return {
      ...session,
      monthly_allocation_mode: hasSurvivingOptimisticWrite
        ? 'MANUAL'
        : previousSession.monthly_allocation_mode || session.monthly_allocation_mode,
      monthly_allocations: restored,
    };
  });
}

export function resetSessionAllocationOverlaysForTests(): void {
  overlays.clear();
  epoch = 0;
}
